import * as React from 'react';
import { FormattedDate } from '@css-xp/base';
import { Promotion, Benefit, SubBenefitType, BillingFrequency, Maybe } from '@css-xp/graphtypes';
import { PromotionDuration } from '@atlassian/cssxp-types-promotion';
import { identity } from 'lodash';

enum INVALID_PROMO_TYPE {
  NOT_AVAILABLE = 'Not available',
}

enum SUB_BENEFIT_TYPE {
  TRIAL = 'TRIAL',
}

export const getReadablePromotionTitle = (promotion: Maybe<Promotion>) => {
  return <span>{promotion?.title || INVALID_PROMO_TYPE.NOT_AVAILABLE}</span>;
};

export const getReadablePromotionValue = (promotion: Maybe<Promotion>) => {
  if (!promotion) {
    return INVALID_PROMO_TYPE.NOT_AVAILABLE;
  }

  if (promotion.benefits?.[0]?.subBenefitType === SUB_BENEFIT_TYPE.TRIAL) {
    return promotion.benefits[0].value + ' Days (' + promotion.benefits[0].subBenefitType + ')';
  }

  const benefitLength = promotion.benefits?.length ?? 0;
  return promotion.benefits?.map((benefit: Maybe<Benefit>, index: number) => {
    const promoBenefits =
      benefit?.duration === PromotionDuration.REPEATING
        ? benefit?.iterations + ' Billing cycles'
        : benefit?.duration ?? '';
    const lowerCaseSubBenefitType = benefit?.subBenefitType?.toLowerCase() ?? '';
    const subBenefitTypeText =
      SubBenefitType?.PERCENTAGE?.toLowerCase() === lowerCaseSubBenefitType
        ? '% Discount - ' + promoBenefits
        : ` (${lowerCaseSubBenefitType})`;

    return (
      <>
        <span key={index}>{`${benefit?.value}${subBenefitTypeText}` ?? INVALID_PROMO_TYPE.NOT_AVAILABLE}</span>
        {index !== benefitLength - 1 && <span key={index}>,</span>}
      </>
    );
  });
};

export const getReadablePromotionWindow = (
  promotion: Maybe<Promotion>,
  promotionStartDate: number | undefined | null = null,
  promotionFrequency: string | null = null
) => {
  if (promotionStartDate) {
    const periodStartdate = new Date(promotionStartDate);
    const promotionIterations = promotion?.benefits?.reduce(
      (filterValue, next) =>
        (filterValue = (filterValue > (next?.iterations ? next?.iterations : 1) ? filterValue : next?.iterations) ?? 1),
      1
    );
    const promotionEndDate =
      promotionFrequency && promotionFrequency === BillingFrequency.ANNUAL
        ? new Date(periodStartdate.setFullYear(periodStartdate.getFullYear() + (promotionIterations ?? 1)))
        : new Date(periodStartdate.setMonth(periodStartdate.getMonth() + (promotionIterations ?? 1)));
    return (
      <>
        <FormattedDate date={promotionStartDate.toString()} /> to{' '}
        <FormattedDate date={promotionEndDate.getTime()?.toString()} />{' '}
      </>
    );
  }
  return (
    <span>
      {promotion?.promotionWindow?.startTime ? (
        <FormattedDate date={promotion.promotionWindow.startTime} />
      ) : (
        INVALID_PROMO_TYPE.NOT_AVAILABLE
      )}
      {promotion?.promotionWindow?.endTime ? (
        <>
          {' - '}
          <FormattedDate date={promotion.promotionWindow.endTime} />
        </>
      ) : (
        INVALID_PROMO_TYPE.NOT_AVAILABLE
      )}
    </span>
  );
};
